import type { Sku } from '@commercelayer/sdk'
import type { ProductSchema } from 'integration-layer/.nuxt/armani/product-schema'
import type { MyAccountWishlistProductCardProps } from '@design-system/components/MyAccount/Wishlist/MyAccountWishlistProductCard.props'
import type { Brand } from '@design-system/index'
import { wishlistStorageItemSchema } from '@integration-layer/schemas/wishlist'
import { countries } from '@design-system/configs/countries'
import { z } from 'zod'
import kebabCase from 'lodash/kebabCase'
import { extractCategoriesUnlocalized } from '@design-system/utils/formatAlgoliaHierarchicalCategories'
import { calculateGADiscountAmount } from '@design-system/utils/googleAnalyticsEcommerce'
import { getOnlineAvailability } from '@design-system/utils/onlineAvailability'

/**
 * Represents a wishlist item as displayed in the My Account Wishlist Product Card.
 */
export type WishlistItem = MyAccountWishlistProductCardProps
/**
 * Represents a wishlist item as stored in local storage or backend.
 * @typedef {z.infer<typeof wishlistStorageItemSchema>} WishlistStorageItem
 */
export type WishlistStorageItem = z.infer<typeof wishlistStorageItemSchema>
/**
 * Interface for wishlist storage operations.
 * @typedef {Object} WishlistStorage
 * @property {() => Promise<WishlistStorageItem[]>} get - Retrieves the wishlist items.
 * @property {(items: WishlistStorageItem[]) => Promise<void>} createOrUpdate - Creates or updates the wishlist items.
 * @property {() => Promise<void>} delete - Deletes the wishlist.
 */
export type WishlistStorage = {
  get: () => Promise<WishlistStorageItem[]>
  createOrUpdate: (items: WishlistStorageItem[]) => Promise<void>
  delete: () => Promise<void>
}
/**
 * Result of diffing wishlist items.
 * @typedef {Object} DiffItemsResult
 * @property {WishlistItem | null} removedItem - The item that was removed, if any.
 * @property {WishlistItem[]} updatedItems - The updated list of items.
 */
type DiffItemsResult = {
  removedItem: WishlistItem | null
  updatedItems: WishlistItem[]
}

/**
 * Authenticated user's wishlist storage operations using API endpoints.
 * @type {WishlistStorage}
 */
export const authWishlistStorage = {
  get: async () => {
    const wishlist =
      (await $fetch<WishlistStorageItem[]>('/api/wishlist')) ?? []

    return wishlist.filter(
      (item: unknown): item is WishlistStorageItem =>
        wishlistStorageItemSchema.safeParse(item).success
    )
  },
  createOrUpdate: (items: WishlistStorageItem[]) =>
    $fetch('/api/wishlist', {
      method: 'post',
      body: {
        wishlists: items,
      },
    }),
  delete: () =>
    $fetch('/api/wishlist', {
      method: 'delete',
    }),
}

/**
 * Guest user's wishlist storage operations using localStorage.
 * @type {WishlistStorage}
 */
export const guestWishlistStorage = {
  /**
   * Retrieves the wishlist items from localStorage.
   * @returns {Promise<WishlistStorageItem[]>} The wishlist items.
   */
  get: async (): Promise<WishlistStorageItem[]> => {
    const stringifiedWishlist = localStorage.getItem('wishlist')
    if (!stringifiedWishlist) return []
    try {
      const wishlist = JSON.parse(stringifiedWishlist)
      return wishlist.filter(
        (item: unknown): item is WishlistStorageItem =>
          wishlistStorageItemSchema.safeParse(item).success
      )
    } catch (err) {
      console.error(err)
      return []
    }
  },
  /**
   * Creates or updates the wishlist items in localStorage.
   * @param {WishlistStorageItem[]} items - The wishlist items to store.
   */
  createOrUpdate: async (items: WishlistStorageItem[]) =>
    localStorage.setItem('wishlist', JSON.stringify(items)),
  /**
   * Deletes the wishlist from localStorage.
   */
  delete: async () => localStorage.removeItem('wishlist'),
}

/**
 * Checks if a product is present in the wishlist.
 * @param {WishlistStorageItem[]} wishlist - The wishlist items.
 * @param {string} productCode - The product code or SKU to check.
 * @returns {boolean} True if the product is in the wishlist, false otherwise.
 */
export const isInWishlist = (
  wishlist: WishlistStorageItem[],
  productCode: string
) =>
  wishlist.some(
    wishlistItem =>
      (wishlistItem.size?.skuCode ?? wishlistItem.productCode) === productCode
  )

/**
 * Returns wishlist items that are present in the guest wishlist but not in the logged-in user's wishlist.
 * @param {WishlistStorageItem[]} guestWishlist - The guest user's wishlist.
 * @param {WishlistStorageItem[]} loggedWishlist - The logged-in user's wishlist.
 * @returns {WishlistStorageItem[]} The items to be added to the logged-in user's wishlist.
 */
export const diffWishlists = (
  guestWishlist: WishlistStorageItem[],
  loggedWishlist: WishlistStorageItem[]
): WishlistStorageItem[] =>
  guestWishlist.filter(item => {
    const productCode = item.size?.skuCode ?? item.productCode
    return !isInWishlist(loggedWishlist, productCode)
  })

/**
 * Finds and removes an item from the wishlist by product code.
 * @param {WishlistItem[]} items - The wishlist items.
 * @param {string} productCode - The product code to remove.
 * @returns {DiffItemsResult} The result with the removed item and updated items.
 */
export const diffItems = (items: WishlistItem[], productCode: string) =>
  items.reduce<DiffItemsResult>(
    (acc, item) => {
      if (item.productCode === productCode) {
        acc.removedItem = item
        return acc
      }
      acc.updatedItems.push(item)
      return acc
    },
    { removedItem: null, updatedItems: [] }
  )

/**
 * Formats a brand string to kebab-case, with special handling for 'ea7'.
 * @param {string} brand - The brand name.
 * @returns {Brand} The formatted brand.
 */
export const formatBrand = (brand: string) => {
  brand = brand.toLowerCase()
  // lodash treats numbers as separate words, so ea7 would be parsed in ea-7
  return (brand === 'ea7' ? brand : kebabCase(brand)) as Brand
}

/**
 * Maps wishlist items to storage format for saving.
 * @param {WishlistItem[]} items - The wishlist items.
 * @param {string} country - The country code.
 * @returns {WishlistStorageItem[]} The mapped storage items.
 */
export const mapStorageItems = (
  items: WishlistItem[],
  country: string
): WishlistStorageItem[] =>
  items.map(item => ({
    productCode: item.productCode,
    website: formatBrand(item.productBrand),
    ...(item.productSku && item.selectedSize
      ? {
          size: {
            skuCode: item.productSku,
            value: item.selectedSize,
            country: item.selectedCountry ?? '',
          },
          notifyMe: true,
        }
      : {}),
    country,
  }))

/**
 * Maps wishlist storage items to display items, enriching with product and SKU data.
 * @param {WishlistStorageItem[]} wishlist - The wishlist storage items.
 * @param {ProductSchema[]} algoliaProducts - The Algolia product data.
 * @param {Sku[]} skus - The SKU data.
 * @param {string} country - The country code.
 * @param {string} language - The language code.
 * @returns {WishlistItem[]} The mapped wishlist items for display.
 */
export const mapWishlistItems = (
  wishlist: WishlistStorageItem[],
  algoliaProducts: ProductSchema[],
  skus: Sku[],
  country: string,
  language: string
) =>
  wishlist.flatMap<WishlistItem>(item => {
    const algoliaProduct = algoliaProducts.find(
      algoliaProduct => algoliaProduct?.objectID === item.productCode
    )
    if (!algoliaProduct) return []

    let currency = ''

    const sku = skus.find(
      sku =>
        sku.code ===
        (item.size?.skuCode ? item.size.skuCode : algoliaProduct.size[0].SKU)
    )
    const skuPrice = sku?.prices?.[0]

    const price = (() => {
      const _country = countries.find(({ code }) => code === country)
      currency = skuPrice?.currency_code ?? _country?.cl?.currency ?? 'EUR'
      return skuPrice?.amount_float ?? algoliaProduct.FullPrice
    })()

    const discountAmount = calculateGADiscountAmount({
      newPrice: skuPrice?.amount_float,
      originalPrice: skuPrice?.compare_at_amount_float,
    })

    const isDiscounted = discountAmount > 0

    const inStock = skus.some(el =>
      item.size?.skuCode
        ? item.size.skuCode === el.code &&
          el.stock_items?.some(item => item.quantity > 0)
        : algoliaProduct.size.find(product => product.SKU === el.code) &&
          el.stock_items?.some(item => item.quantity > 0)
    )
    const { isAvailable, notAvailableOnline } = getOnlineAvailability(
      inStock,
      algoliaProduct.isFallback
    )

    const lastInStock = skus.some(el =>
      item.size?.skuCode
        ? item.size.skuCode === el.code &&
          !el?.stock_items?.some(item => item.quantity > 1)
        : algoliaProduct.size.some(
            size =>
              size.SKU === el.code &&
              !el?.stock_items?.some(item => item.quantity > 1)
          )
    )

    const categories = extractCategoriesUnlocalized(
      algoliaProduct?.hierarchicalCategories || [],
      algoliaProduct?.categoryPageId2 || []
    )

    const selectedImage =
      getHighestPriorityImage(algoliaProduct.assets)?.URL ?? ''

    return {
      productSku: item.size?.skuCode,
      productCode: item.productCode,
      title: algoliaProduct.Name,
      path: getPdpPath(algoliaProduct, language),
      selectedColor: algoliaProduct.ColorDesc ?? '',
      selectedSize: item.size?.value,
      selectedCountry: item.size?.country,
      productBrand: item.website as Brand,
      selectedImage,
      price,
      currency,
      categories,
      isAvailable,
      notAvailableOnline,
      isDiscounted,
      discountAmount,
      lastInStock,
      MFC: algoliaProduct?.MFC ?? '',
      notifyMe: !!item.notifyMe,
      nameURL: algoliaProduct.NameURL,
    }
  })
